import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Users,
  Plus,
  X,
  Mail,
  Send,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/CompleteAuthContext';

const CreateGroup = ({ onBack }) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    avatar: ''
  });

  // Helper to get display name with fallback
  const getDisplayName = (userData) => {
    if (!userData) return 'User';
    if (userData.full_name?.trim()) return userData.full_name.trim();
    if (userData.name?.trim()) return userData.name.trim();
    if (userData.email) return userData.email.split('@')[0];
    return 'User';
  };

  // Initialize owner member from authenticated user
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (user) {
      // Set the current user as the owner
      setMembers([{
        id: user.id || 'current_user',
        name: getDisplayName(user),
        email: user.email,
        avatar: user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(getDisplayName(user))}&background=4F46E5&color=fff`,
        isOwner: true
      }]);
    }
  }, [user]);

  const [newMember, setNewMember] = useState({ name: '', email: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAddMember = () => {
    if (!newMember.name.trim() || !newMember.email.trim()) {
      toast.error('Please fill in both name and email');
      return;
    }

    // Check if email already exists
    if (members.some(m => m.email.toLowerCase() === newMember.email.toLowerCase())) {
      toast.error('This email is already added to the group');
      return;
    }

    const member = {
      id: Date.now().toString(),
      name: newMember.name.trim(),
      email: newMember.email.trim().toLowerCase(),
      avatar: `https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face`,
      isOwner: false
    };

    setMembers(prev => [...prev, member]);
    setNewMember({ name: '', email: '' });
    toast.success(`${member.name} added to the group`);
  };

  const handleRemoveMember = (memberId) => {
    if (members.find(m => m.id === memberId)?.isOwner) {
      toast.error("You can't remove yourself as the group owner");
      return;
    }

    setMembers(prev => prev.filter(m => m.id !== memberId));
    toast.success('Member removed from the group');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    // Removed minimum members requirement - owner is auto-added by backend
    // Users can now create empty groups and add members later

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Prepare members list (exclude owner, they're added automatically by backend)
      const invitedMembers = members
        .filter(m => !m.isOwner)
        .map(m => ({
          name: m.name,
          email: m.email
        }));
      
      const response = await fetch(`${BACKEND_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || '',
          members: invitedMembers
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create group');
      }

      const newGroup = await response.json();
      
      toast.success('Group created successfully!', {
        description: `${formData.name} has been created with ${members.length} members. ${invitedMembers.length > 0 ? 'Invitation emails sent!' : ''}`
      });
      
      setIsLoading(false);
      onBack();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group', {
        description: error.message || 'Please try again'
      });
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMember();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack} size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Create New Group</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Group Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Send className="w-5 h-5" />
                <span>Group Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Group Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Weekend Trip, Roommates, Office Lunch"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description of the group"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Group Members</span>
                </div>
                <Badge variant="secondary">
                  {members.length} member{members.length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add Member Form */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-blue-900">Add New Member</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="memberName" className="text-blue-800">Name</Label>
                        <Input
                          id="memberName"
                          placeholder="Enter member's name"
                          value={newMember.name}
                          onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                          onKeyPress={handleKeyPress}
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="memberEmail" className="text-blue-800">Email</Label>
                        <Input
                          id="memberEmail"
                          type="email"
                          placeholder="Enter email address"
                          value={newMember.email}
                          onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                          onKeyPress={handleKeyPress}
                          className="bg-white"
                        />
                      </div>
                    </div>
                    <Button 
                      type="button" 
                      onClick={handleAddMember}
                      className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                      disabled={!newMember.name.trim() || !newMember.email.trim()}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Member
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Members List */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Current Members</h4>
                <div className="space-y-3">
                  {members.map((member) => (
                    <div 
                      key={member.id}
                      className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={member.avatar} alt={member.name} />
                          <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900">{member.name}</p>
                            {member.isOwner && (
                              <Badge variant="default" className="bg-blue-100 text-blue-700 text-xs">
                                Owner
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {member.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {member.isOwner ? (
                          <div className="p-2 bg-green-100 rounded-full">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {errors.members && (
                  <p className="text-sm text-red-600">{errors.members}</p>
                )}
              </div>

              {/* Group Summary */}
              {formData.name.trim() && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Group Summary</h4>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p>• Group name: "{formData.name}"</p>
                    <p>• Total members: {members.length} {members.length === 1 ? '(you)' : `(you + ${members.length - 1} invited)`}</p>
                    {members.length === 1 && (
                      <p>• You can add members now or later from the group page</p>
                    )}
                    {members.length > 1 && (
                      <p>• Invitation emails will be sent to all members</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex items-center space-x-4 pt-6">
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700 px-8"
              disabled={isLoading || !formData.name.trim()}
            >
              {isLoading ? 'Creating Group...' : 'Create Group'}
            </Button>
            <Button type="button" variant="outline" onClick={onBack} disabled={isLoading}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroup;